import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Search, Edit2, Trash2, TrendingUp, TrendingDown, Users, CreditCard, RotateCw, Clock } from 'lucide-react';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip, Legend } from 'chart.js';
import apiService from '../../services/ApiService';
import TransactionModal from './TransactionModal';
import RecurringModal from './RecurringModal';
import { useApp } from '../../context/AppContext';
import { isManagerOrAbove } from '../../utils/roleHelpers';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

// ── Design tokens ─────────────────────────────────────────────────────────────
const FONT = '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Helvetica Neue", sans-serif';
const BG = '#F2F2F7';
const CARD = { background: '#fff', borderRadius: 18, boxShadow: '0 1px 2px rgba(0,0,0,0.04), 0 4px 20px rgba(0,0,0,0.07)', fontFamily: FONT };
const MUTED = '#8E8E93';
const INK = '#1C1C1E';
const SECONDARY = '#3C3C43';
const SYS_GREEN  = '#34C759';
const SYS_RED    = '#FF3B30';
const SYS_BLUE   = '#007AFF';
const SYS_ORANGE = '#FF9500';
const SYS_PURPLE = '#AF52DE';
const BRAND      = '#84CC16';

// ── Formatters ────────────────────────────────────────────────────────────────
function fmt(n)  { return '$' + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
function fmtK(n) { return Math.abs(n) >= 1000 ? '$' + (n / 1000).toFixed(1) + 'k' : '$' + Number(n || 0).toFixed(0); }
function pct(a, b) { return b > 0 ? ((a / b) * 100).toFixed(1) + '%' : '—'; }

// ── Period helpers ────────────────────────────────────────────────────────────
function periodFilter(txns, period) {
  const now = new Date();
  const iso = d => d.toISOString().slice(0, 10);
  if (period === 'week') {
    const s = new Date(now); s.setDate(s.getDate() - s.getDay());
    return txns.filter(t => t.date >= iso(s));
  }
  if (period === 'month')     return txns.filter(t => t.date >= iso(new Date(now.getFullYear(), now.getMonth(), 1)));
  if (period === 'lastmonth') {
    const s = iso(new Date(now.getFullYear(), now.getMonth() - 1, 1));
    const e = iso(new Date(now.getFullYear(), now.getMonth(), 0));
    return txns.filter(t => t.date >= s && t.date <= e);
  }
  if (period === 'quarter') {
    const q = Math.floor(now.getMonth() / 3);
    return txns.filter(t => t.date >= iso(new Date(now.getFullYear(), q * 3, 1)));
  }
  if (period === 'year') return txns.filter(t => t.date >= iso(new Date(now.getFullYear(), 0, 1)));
  return txns;
}

function prevPeriodFilter(txns, period) {
  const now = new Date();
  const iso = d => d.toISOString().slice(0, 10);
  if (period === 'month') {
    const s = iso(new Date(now.getFullYear(), now.getMonth() - 1, 1));
    const e = iso(new Date(now.getFullYear(), now.getMonth(), 0));
    return txns.filter(t => t.date >= s && t.date <= e);
  }
  if (period === 'quarter') {
    const q = Math.floor(now.getMonth() / 3);
    const s = iso(new Date(now.getFullYear(), (q - 1) * 3, 1));
    const e = iso(new Date(now.getFullYear(), q * 3, 0));
    return txns.filter(t => t.date >= s && t.date <= e);
  }
  if (period === 'year') {
    const s = iso(new Date(now.getFullYear() - 1, 0, 1));
    const e = iso(new Date(now.getFullYear() - 1, 11, 31));
    return txns.filter(t => t.date >= s && t.date <= e);
  }
  return [];
}

// overhead months for the selected period
function overheadMonths(period) {
  const now = new Date();
  if (period === 'week')      return 7 / 30.4;
  if (period === 'month')     return 1;
  if (period === 'lastmonth') return 1;
  if (period === 'quarter')   return 3;
  if (period === 'year')      return now.getMonth() + 1;
  return null; // 'all' — show monthly rate, not total
}

// ── Sub-components ────────────────────────────────────────────────────────────
function Delta({ current, previous, invert = false }) {
  if (!previous || previous === 0) return null;
  const d = ((current - previous) / previous) * 100;
  const good = invert ? d <= 0 : d >= 0;
  return (
    <span style={{ fontSize: 12, fontWeight: 600, color: good ? SYS_GREEN : SYS_RED, fontFamily: FONT }}>
      {d >= 0 ? '↑' : '↓'} {Math.abs(d).toFixed(1)}% vs prev
    </span>
  );
}

function KpiCard({ label, value, sub, delta, deltaInvert, accent, small }) {
  return (
    <div style={{ ...CARD, padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.07em' }}>{label}</div>
      <div style={{ fontSize: small ? 22 : 30, fontWeight: 700, color: accent || INK, fontVariantNumeric: 'tabular-nums', lineHeight: 1.1 }}>{value}</div>
      {delta && <div>{delta}</div>}
      {sub && <div style={{ fontSize: 12, color: MUTED }}>{sub}</div>}
    </div>
  );
}

function BarRow({ label, value, total, color = BRAND, valueFmt }) {
  const p = total > 0 ? Math.min(100, (value / total) * 100) : 0;
  return (
    <div style={{ marginBottom: 15 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 5 }}>
        <span style={{ fontSize: 13, color: SECONDARY, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '55%' }}>{label}</span>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: INK }}>{valueFmt || fmt(value)}</span>
          <span style={{ fontSize: 11, color: MUTED, minWidth: 32, textAlign: 'right' }}>{p.toFixed(0)}%</span>
        </div>
      </div>
      <div style={{ height: 4, background: '#E5E5EA', borderRadius: 2, overflow: 'hidden' }}>
        <div style={{ height: '100%', background: color, width: `${p}%`, borderRadius: 2, transition: 'width 0.5s cubic-bezier(0.4,0,0.2,1)' }} />
      </div>
    </div>
  );
}

function Badge(type) {
  const map = {
    sale:    [SYS_GREEN,  '#F0FDF4'],
    expense: [SYS_RED,    '#FFF1F0'],
    refund:  [SYS_ORANGE, '#FFF8ED'],
    paid:    [SYS_GREEN,  '#F0FDF4'],
    pending: [SYS_ORANGE, '#FFF8ED'],
    overdue: [SYS_RED,    '#FFF1F0'],
  };
  const [color, bg] = map[type] || [MUTED, '#F3F4F6'];
  return <span style={{ padding: '3px 9px', borderRadius: 7, fontSize: 11, fontWeight: 600, color, background: bg, fontFamily: FONT }}>{type}</span>;
}

function SectionTitle({ children, action }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
      <h3 style={{ fontSize: 15, fontWeight: 700, color: INK, margin: 0, fontFamily: FONT }}>{children}</h3>
      {action}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function FinancialPage() {
  const { currentUser } = useApp();
  const [tab, setTab] = useState('overview');
  const [transactions, setTransactions] = useState([]);
  const [recurring, setRecurring] = useState([]);
  const [loading, setLoading] = useState(true);
  const [txnModal, setTxnModal] = useState(null);
  const [recurModal, setRecurModal] = useState(null);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [period, setPeriod] = useState('month');
  const [error, setError] = useState('');

  const canReport = isManagerOrAbove(currentUser) || currentUser?.role === 'Finance';

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [txns, recs] = await Promise.all([apiService.getTransactions(), apiService.getRecurring()]);
      setTransactions(txns);
      setRecurring(recs);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // ── Metrics ───────────────────────────────────────────────────────────────
  const periodTxns = periodFilter(transactions, period);
  const prevTxns   = prevPeriodFilter(transactions, period);

  const paidSales   = periodTxns.filter(t => t.type === 'sale' && t.status === 'paid');
  const allSales    = periodTxns.filter(t => t.type === 'sale');
  const revenue     = paidSales.reduce((s, t) => s + t.amount, 0);
  const jobExpenses = periodTxns.filter(t => t.type === 'expense').reduce((s, t) => s + t.amount, 0);
  const refunds     = periodTxns.filter(t => t.type === 'refund').reduce((s, t) => s + t.amount, 0);
  const grossProfit = revenue - jobExpenses - refunds;

  const monthlyOverhead = recurring.filter(r => r.active !== false)
    .reduce((s, r) => s + (r.frequency === 'monthly' ? r.amount : r.amount / 12), 0);
  const oMonths = overheadMonths(period);
  const overheadForPeriod = oMonths !== null ? monthlyOverhead * oMonths : monthlyOverhead;

  const netProfit    = grossProfit - overheadForPeriod;
  const grossMargin  = revenue > 0 ? (grossProfit / revenue) * 100 : 0;
  const netMargin    = revenue > 0 ? (netProfit / revenue) * 100 : 0;
  const avgSale      = paidSales.length > 0 ? revenue / paidSales.length : 0;

  // ── Daily metrics ─────────────────────────────────────────────────────────
  const now = new Date();
  const daysElapsedThisMonth = now.getDate();
  const daysInCurrentMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();

  function getPeriodDays(p, txns) {
    if (p === 'week')      return 7;
    if (p === 'month')     return daysElapsedThisMonth;
    if (p === 'lastmonth') return new Date(now.getFullYear(), now.getMonth(), 0).getDate();
    if (p === 'quarter')   return Math.ceil((now - new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1)) / 864e5) + 1;
    if (p === 'year')      return Math.ceil((now - new Date(now.getFullYear(), 0, 1)) / 864e5) + 1;
    if (txns.length === 0) return 30;
    const dates = txns.map(t => t.date).sort();
    return Math.max(1, Math.ceil((new Date(dates[dates.length - 1]) - new Date(dates[0])) / 864e5) + 1);
  }

  const periodDays       = getPeriodDays(period, periodTxns);
  const dailyRevenue     = periodDays > 0 ? revenue / periodDays : 0;
  const dailyJobCosts    = periodDays > 0 ? jobExpenses / periodDays : 0;
  const dailyOverhead    = monthlyOverhead / 30.4;
  const dailyGross       = dailyRevenue - dailyJobCosts;
  const dailyNet         = dailyGross - dailyOverhead;
  const projectedMonthRevenue = period === 'month' && daysElapsedThisMonth > 0
    ? (revenue / daysElapsedThisMonth) * daysInCurrentMonth : null;
  const projectedMonthProfit = projectedMonthRevenue !== null
    ? projectedMonthRevenue - (jobExpenses / daysElapsedThisMonth) * daysInCurrentMonth - monthlyOverhead : null;
  const breakevenDay = period === 'month' && dailyGross > 0
    ? Math.ceil(monthlyOverhead / dailyGross) : null;

  // ── Due this month ────────────────────────────────────────────────────────
  const currentMonth = now.getMonth() + 1;
  const currentDay   = now.getDate();
  const dueThisMonth = recurring.filter(r => {
    if (r.active === false) return false;
    if (r.frequency === 'monthly' && r.dueDay) return true;
    if (r.frequency === 'annual' && r.dueMonth === currentMonth && r.dueDay) return true;
    return false;
  }).sort((a, b) => (a.dueDay || 99) - (b.dueDay || 99));

  const pending      = periodTxns.filter(t => t.status === 'pending' || t.status === 'overdue');
  const pendingTotal = pending.reduce((s, t) => s + t.amount, 0);
  const totalBilled  = revenue + pendingTotal;
  const collRate     = totalBilled > 0 ? (revenue / totalBilled) * 100 : 100;

  const prevRev  = prevTxns.filter(t => t.type === 'sale' && t.status === 'paid').reduce((s, t) => s + t.amount, 0);
  const prevExp  = prevTxns.filter(t => t.type === 'expense').reduce((s, t) => s + t.amount, 0);
  const prevProfit = prevRev - prevExp - monthlyOverhead * (oMonths || 1);

  // top customers
  const custMap = {};
  paidSales.forEach(t => { const n = t.customerName?.trim() || 'Unknown'; custMap[n] = (custMap[n] || 0) + t.amount; });
  const topCust = Object.entries(custMap).sort((a, b) => b[1] - a[1]).slice(0, 8);

  // revenue by category
  const revByCat = {};
  paidSales.forEach(t => { revByCat[t.category || 'Other'] = (revByCat[t.category || 'Other'] || 0) + t.amount; });
  const revCatList = Object.entries(revByCat).sort((a, b) => b[1] - a[1]);

  // expense by category
  const expByCat = {};
  periodTxns.filter(t => t.type === 'expense').forEach(t => { expByCat[t.category || 'Other'] = (expByCat[t.category || 'Other'] || 0) + t.amount; });
  const expCatList = Object.entries(expByCat).sort((a, b) => b[1] - a[1]);

  // payment methods
  const payMap = {};
  paidSales.forEach(t => { payMap[t.paymentMethod] = (payMap[t.paymentMethod] || 0) + t.amount; });

  // overhead by category
  const recByCat = {};
  recurring.filter(r => r.active !== false).forEach(r => {
    const mo = r.frequency === 'monthly' ? r.amount : r.amount / 12;
    recByCat[r.category || 'Other'] = (recByCat[r.category || 'Other'] || 0) + mo;
  });

  // 6-month chart
  const chartLabels = [], chartRev = [], chartExp = [], chartNet = [];
  for (let i = 5; i >= 0; i--) {
    const d = new Date(); d.setMonth(d.getMonth() - i);
    const ms = new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
    const me = new Date(d.getFullYear(), d.getMonth() + 1, 0).toISOString().slice(0, 10);
    chartLabels.push(d.toLocaleString('default', { month: 'short' }));
    const rev = transactions.filter(t => t.type === 'sale' && t.status === 'paid' && t.date >= ms && t.date <= me).reduce((s, t) => s + t.amount, 0);
    const exp = transactions.filter(t => t.type === 'expense' && t.date >= ms && t.date <= me).reduce((s, t) => s + t.amount, 0);
    chartRev.push(rev);
    chartExp.push(exp);
    chartNet.push(rev - exp - monthlyOverhead);
  }

  const chartData = {
    labels: chartLabels,
    datasets: [
      { label: 'Revenue',    data: chartRev, backgroundColor: 'rgba(132,204,22,0.75)', borderRadius: 6, borderSkipped: false },
      { label: 'Job Costs',  data: chartExp, backgroundColor: 'rgba(255,59,48,0.65)',  borderRadius: 6, borderSkipped: false },
      { label: 'Net Profit', data: chartNet, backgroundColor: 'rgba(0,122,255,0.75)',  borderRadius: 6, borderSkipped: false },
    ],
  };
  const chartOpts = {
    responsive: true, maintainAspectRatio: false,
    plugins: {
      legend: { position: 'bottom', labels: { font: { family: FONT, size: 12 }, boxWidth: 12, padding: 16 } },
      tooltip: { callbacks: { label: ctx => ` ${ctx.dataset.label}: ${fmtK(ctx.parsed.y)}` } },
    },
    scales: {
      x: { grid: { display: false }, ticks: { font: { family: FONT, size: 12 }, color: MUTED } },
      y: { grid: { color: 'rgba(0,0,0,0.04)', drawBorder: false }, ticks: { font: { family: FONT, size: 11 }, color: MUTED, callback: v => fmtK(v) } },
    },
  };

  // ── Transaction list ──────────────────────────────────────────────────────
  const filtered = transactions.filter(t => {
    if (typeFilter !== 'all' && t.type !== typeFilter) return false;
    if (statusFilter !== 'all' && t.status !== statusFilter) return false;
    if (search && !t.description?.toLowerCase().includes(search.toLowerCase()) && !t.customerName?.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  }).sort((a, b) => b.date.localeCompare(a.date));

  // ── Handlers ──────────────────────────────────────────────────────────────
  async function handleSaveTxn(form) {
    if (txnModal === 'new') {
      const created = await apiService.createTransaction(form);
      setTransactions(prev => [...prev, created]);
    } else {
      const updated = await apiService.updateTransaction(form.id, form);
      setTransactions(prev => prev.map(t => t.id === updated.id ? updated : t));
    }
  }

  async function handleDeleteTxn(txn) {
    if (!window.confirm(`Delete "${txn.description}"? This cannot be undone.`)) return;
    await apiService.deleteTransaction(txn.id);
    setTransactions(prev => prev.filter(t => t.id !== txn.id));
  }

  async function handleSaveRecurring(form) {
    if (recurModal === 'new') {
      const created = await apiService.createRecurring(form);
      setRecurring(prev => [...prev, created]);
    } else {
      const updated = await apiService.updateRecurring(form.id, form);
      setRecurring(prev => prev.map(r => r.id === updated.id ? updated : r));
    }
  }

  async function handleDeleteRecurring(item) {
    if (!window.confirm(`Remove "${item.name}" from overhead? This cannot be undone.`)) return;
    await apiService.deleteRecurring(item.id);
    setRecurring(prev => prev.filter(r => r.id !== item.id));
  }

  // ── UI helpers ────────────────────────────────────────────────────────────
  const PERIOD_OPTS = [['week','Week'],['month','Month'],['lastmonth','Last Mo.'],['quarter','Quarter'],['year','Year'],['all','All Time']];

  const TABS = [
    ['overview', 'Overview'],
    ['transactions', `Transactions`],
    ['overhead', 'Overhead'],
    ...(canReport ? [['reports', 'Reports']] : []),
  ];

  function PeriodPicker() {
    return (
      <div style={{ display: 'flex', gap: 0, background: '#E5E5EA', borderRadius: 11, padding: 3 }}>
        {PERIOD_OPTS.map(([val, label]) => (
          <button key={val} onClick={() => setPeriod(val)} style={{
            flex: 1, padding: '7px 4px', borderRadius: 9, border: 'none',
            background: period === val ? '#fff' : 'transparent',
            boxShadow: period === val ? '0 1px 4px rgba(0,0,0,0.15)' : 'none',
            color: period === val ? INK : MUTED,
            fontWeight: period === val ? 600 : 400,
            cursor: 'pointer', fontSize: 11, fontFamily: FONT,
            transition: 'all 0.15s ease',
          }}>{label}</button>
        ))}
      </div>
    );
  }

  if (loading) return (
    <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: BG, fontFamily: FONT }}>
      <div style={{ color: MUTED, fontSize: 14 }}>Loading…</div>
    </div>
  );

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', background: BG, fontFamily: FONT }}>
      {error && <div style={{ background: '#FFF1F0', color: SYS_RED, padding: '10px 20px', fontSize: 13 }}>{error}</div>}

      {/* ── Tab bar ── */}
      <div style={{ background: '#fff', borderBottom: '1px solid rgba(0,0,0,0.07)', padding: '10px 12px', flexShrink: 0 }}>
        <div style={{ display: 'flex', gap: 0, background: '#E5E5EA', borderRadius: 11, padding: 3 }}>
          {TABS.map(([val, label]) => (
            <button key={val} onClick={() => setTab(val)} style={{
              flex: 1, padding: '7px 6px', borderRadius: 9, border: 'none', whiteSpace: 'nowrap',
              background: tab === val ? '#fff' : 'transparent',
              boxShadow: tab === val ? '0 1px 4px rgba(0,0,0,0.15)' : 'none',
              color: tab === val ? INK : MUTED,
              fontWeight: tab === val ? 600 : 400,
              cursor: 'pointer', fontSize: 12, fontFamily: FONT,
              transition: 'all 0.15s ease',
            }}>{label}</button>
          ))}
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>

        {/* ══ OVERVIEW ════════════════════════════════════════════════════════ */}
        {tab === 'overview' && (
          <div>
            <div style={{ marginBottom: 20 }}><PeriodPicker /></div>

            {/* KPI cards row 1 */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(155px, 1fr))', gap: 14, marginBottom: 14 }}>
              <KpiCard label="Revenue" value={fmt(revenue)}
                delta={<Delta current={revenue} previous={prevRev} />}
                accent={SYS_GREEN} />
              <KpiCard label="Job Costs" value={fmt(jobExpenses)}
                delta={<Delta current={jobExpenses} previous={prevExp} invert />}
                accent={SYS_RED} />
              <KpiCard label="Fixed Overhead" value={fmt(overheadForPeriod)}
                sub={oMonths ? `${fmt(monthlyOverhead)}/mo` : `${fmt(monthlyOverhead)}/mo rate`}
                accent={SYS_ORANGE} />
            </div>

            {/* KPI cards row 2 */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(155px, 1fr))', gap: 14, marginBottom: 22 }}>
              <KpiCard label="Gross Profit" value={fmt(grossProfit)}
                sub={`${grossMargin.toFixed(1)}% gross margin`}
                accent={grossProfit >= 0 ? BRAND : SYS_RED} />
              <KpiCard label="Net Profit" value={fmt(netProfit)}
                delta={<Delta current={netProfit} previous={prevProfit} />}
                accent={netProfit >= 0 ? SYS_BLUE : SYS_RED} />
              <KpiCard label="Net Margin" value={netMargin.toFixed(1) + '%'}
                sub={`Avg sale ${fmt(avgSale)}`}
                accent={netMargin >= 20 ? SYS_GREEN : netMargin >= 0 ? SYS_ORANGE : SYS_RED}
                small />
            </div>

            {/* Daily Rates card */}
            <div style={{ ...CARD, padding: '20px 22px', marginBottom: 22 }}>
              <SectionTitle>
                <span>Daily Snapshot</span>
                <span style={{ fontSize: 11, color: MUTED }}>{period === 'month' ? `Day ${daysElapsedThisMonth} of ${daysInCurrentMonth}` : `${periodDays} day period`}</span>
              </SectionTitle>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 12 }}>
                {[
                  { label: 'Daily Revenue', val: fmt(dailyRevenue), color: SYS_GREEN },
                  { label: 'Daily Overhead', val: fmt(dailyOverhead), color: SYS_ORANGE },
                  { label: 'Daily Net', val: fmt(dailyNet), color: dailyNet >= 0 ? SYS_BLUE : SYS_RED },
                  ...(projectedMonthRevenue !== null ? [
                    { label: 'Projected Month', val: fmt(projectedMonthRevenue), color: BRAND },
                    { label: 'Proj. Net Profit', val: fmt(projectedMonthProfit), color: projectedMonthProfit >= 0 ? SYS_BLUE : SYS_RED },
                  ] : []),
                  ...(breakevenDay !== null ? [
                    { label: 'Breakeven Day', val: breakevenDay <= daysInCurrentMonth ? `Day ${breakevenDay}` : 'Not this mo.', color: breakevenDay <= currentDay ? SYS_GREEN : SYS_ORANGE },
                  ] : []),
                ].map(({ label, val, color }) => (
                  <div key={label} style={{ background: BG, borderRadius: 12, padding: '14px 16px' }}>
                    <div style={{ fontSize: 10, fontWeight: 600, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6 }}>{label}</div>
                    <div style={{ fontSize: 18, fontWeight: 700, color, fontVariantNumeric: 'tabular-nums' }}>{val}</div>
                  </div>
                ))}
              </div>
              {breakevenDay !== null && (
                <div style={{ marginTop: 14, padding: '10px 14px', borderRadius: 10, background: breakevenDay <= currentDay ? 'rgba(52,199,89,0.08)' : 'rgba(255,149,0,0.08)' }}>
                  <span style={{ fontSize: 12, color: breakevenDay <= currentDay ? SYS_GREEN : SYS_ORANGE, fontWeight: 600 }}>
                    {breakevenDay <= currentDay
                      ? `Overhead covered — you passed breakeven on day ${breakevenDay}`
                      : `Breakeven in ${breakevenDay - currentDay} more days (day ${breakevenDay} of ${daysInCurrentMonth})`}
                  </span>
                </div>
              )}
            </div>

            {/* Chart */}
            <div style={{ ...CARD, padding: '20px 20px 14px', marginBottom: 20 }}>
              <SectionTitle>6-Month Trend</SectionTitle>
              <div style={{ height: 210 }}>
                <Bar data={chartData} options={chartOpts} />
              </div>
            </div>

            {/* Bottom panels */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(270px, 1fr))', gap: 16, marginBottom: 16 }}>
              {/* Top customers */}
              <div style={{ ...CARD, padding: '20px 22px' }}>
                <SectionTitle>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Users size={14} color={SYS_BLUE} /> Top Customers</span>
                  <span style={{ fontSize: 11, color: MUTED }}>by revenue</span>
                </SectionTitle>
                {topCust.length === 0
                  ? <p style={{ color: MUTED, fontSize: 13 }}>No sales yet.</p>
                  : topCust.map(([name, total], i) => (
                    <div key={name} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                      <span style={{ width: 22, height: 22, borderRadius: '50%', background: i === 0 ? '#FEF3C7' : BG, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: i === 0 ? SYS_ORANGE : MUTED, flexShrink: 0 }}>{i + 1}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                          <span style={{ fontSize: 13, color: SECONDARY, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '60%' }}>{name}</span>
                          <span style={{ fontSize: 13, fontWeight: 700, color: SYS_BLUE }}>{fmt(total)}</span>
                        </div>
                        <div style={{ height: 3, background: '#E5E5EA', borderRadius: 2, overflow: 'hidden' }}>
                          <div style={{ height: '100%', background: i === 0 ? SYS_ORANGE : SYS_BLUE, width: `${(total / topCust[0][1]) * 100}%`, borderRadius: 2 }} />
                        </div>
                      </div>
                    </div>
                  ))}
              </div>

              {/* Revenue by category */}
              <div style={{ ...CARD, padding: '20px 22px' }}>
                <SectionTitle>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><TrendingUp size={14} color={SYS_GREEN} /> Revenue by Category</span>
                </SectionTitle>
                {revCatList.length === 0
                  ? <p style={{ color: MUTED, fontSize: 13 }}>No sales yet.</p>
                  : revCatList.map(([cat, total]) => <BarRow key={cat} label={cat} value={total} total={revenue} color={SYS_GREEN} />)}
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(270px, 1fr))', gap: 16 }}>
              {/* Job expense breakdown */}
              <div style={{ ...CARD, padding: '20px 22px' }}>
                <SectionTitle>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><TrendingDown size={14} color={SYS_RED} /> Job Expenses</span>
                </SectionTitle>
                {expCatList.length === 0
                  ? <p style={{ color: MUTED, fontSize: 13 }}>No expenses yet.</p>
                  : expCatList.map(([cat, total]) => <BarRow key={cat} label={cat} value={total} total={jobExpenses} color={SYS_RED} />)}
              </div>

              {/* Payment methods */}
              <div style={{ ...CARD, padding: '20px 22px' }}>
                <SectionTitle>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><CreditCard size={14} color={SYS_PURPLE} /> Payment Methods</span>
                </SectionTitle>
                {Object.keys(payMap).length === 0
                  ? <p style={{ color: MUTED, fontSize: 13 }}>No payments yet.</p>
                  : Object.entries(payMap).sort((a, b) => b[1] - a[1]).map(([m, total]) => <BarRow key={m} label={m} value={total} total={revenue} color={SYS_PURPLE} />)}

                {/* Collection rate */}
                <div style={{ marginTop: 18, paddingTop: 16, borderTop: '1px solid #F2F2F7' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: 12, color: MUTED, display: 'flex', alignItems: 'center', gap: 4 }}><Clock size={11} /> Collection Rate</span>
                    <span style={{ fontSize: 18, fontWeight: 700, color: collRate >= 90 ? SYS_GREEN : SYS_ORANGE }}>{collRate.toFixed(1)}%</span>
                  </div>
                  {pending.length > 0 && <div style={{ fontSize: 11, color: MUTED, marginTop: 2 }}>{pending.length} pending · {fmt(pendingTotal)}</div>}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ══ TRANSACTIONS ════════════════════════════════════════════════════ */}
        {tab === 'transactions' && (
          <div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
              <div style={{ position: 'relative', flex: 1, minWidth: 180 }}>
                <Search size={14} color={MUTED} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' }} />
                <input type="text" placeholder="Search…" value={search} onChange={e => setSearch(e.target.value)}
                  style={{ width: '100%', padding: '10px 12px 10px 34px', border: 'none', borderRadius: 12, fontSize: 13, outline: 'none', background: '#fff', boxShadow: '0 1px 2px rgba(0,0,0,0.04)', fontFamily: FONT, boxSizing: 'border-box' }} />
              </div>
              <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
                style={{ padding: '10px 12px', border: 'none', borderRadius: 12, fontSize: 13, color: INK, background: '#fff', boxShadow: '0 1px 2px rgba(0,0,0,0.04)', fontFamily: FONT }}>
                <option value="all">All Types</option><option value="sale">Sales</option><option value="expense">Expenses</option><option value="refund">Refunds</option>
              </select>
              <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
                style={{ padding: '10px 12px', border: 'none', borderRadius: 12, fontSize: 13, color: INK, background: '#fff', boxShadow: '0 1px 2px rgba(0,0,0,0.04)', fontFamily: FONT }}>
                <option value="all">All Status</option><option value="paid">Paid</option><option value="pending">Pending</option><option value="overdue">Overdue</option>
              </select>
              <button onClick={() => setTxnModal('new')}
                style={{ padding: '10px 18px', background: BRAND, border: 'none', borderRadius: 12, color: 'white', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontFamily: FONT, whiteSpace: 'nowrap' }}>
                <Plus size={14} /> Add
              </button>
            </div>

            <div style={{ ...CARD, overflow: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #F2F2F7' }}>
                    {['Date','Type','Description','Customer','Amount','Method','Status',''].map(h => (
                      <th key={h} style={{ textAlign: 'left', padding: '12px 14px', color: MUTED, fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap', fontFamily: FONT }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0
                    ? <tr><td colSpan={8} style={{ textAlign: 'center', padding: 40, color: MUTED, fontFamily: FONT }}>No transactions found.</td></tr>
                    : filtered.map(t => (
                      <tr key={t.id} style={{ borderBottom: '1px solid #F9F9F9' }}>
                        <td style={{ padding: '11px 14px', color: MUTED, whiteSpace: 'nowrap', fontSize: 12 }}>{t.date}</td>
                        <td style={{ padding: '11px 14px' }}>{Badge(t.type)}</td>
                        <td style={{ padding: '11px 14px', color: INK, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.description}</td>
                        <td style={{ padding: '11px 14px', color: MUTED, whiteSpace: 'nowrap', fontSize: 12 }}>{t.customerName || '—'}</td>
                        <td style={{ padding: '11px 14px', fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: t.type === 'expense' ? SYS_RED : t.type === 'refund' ? SYS_ORANGE : SYS_GREEN, whiteSpace: 'nowrap' }}>
                          {t.type === 'expense' ? '−' : '+'}{fmt(t.amount)}
                        </td>
                        <td style={{ padding: '11px 14px', color: MUTED, whiteSpace: 'nowrap', fontSize: 12 }}>{t.paymentMethod}</td>
                        <td style={{ padding: '11px 14px' }}>{Badge(t.status)}</td>
                        <td style={{ padding: '11px 14px' }}>
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button onClick={() => setTxnModal(t)} style={{ padding: '5px 10px', border: '1px solid #E5E5EA', borderRadius: 8, background: 'white', cursor: 'pointer', color: SECONDARY, display: 'flex', alignItems: 'center', gap: 3, fontSize: 11, fontFamily: FONT }}><Edit2 size={10} /></button>
                            <button onClick={() => handleDeleteTxn(t)} style={{ padding: '5px 10px', border: '1px solid #FFD0CC', borderRadius: 8, background: 'white', cursor: 'pointer', color: SYS_RED, display: 'flex', alignItems: 'center', gap: 3, fontSize: 11, fontFamily: FONT }}><Trash2 size={10} /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
              <div style={{ padding: '12px 16px', background: BG, borderTop: '1px solid #E5E5EA', fontSize: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, borderRadius: '0 0 18px 18px' }}>
                <span style={{ color: MUTED }}>{filtered.length} transactions</span>
                <div style={{ display: 'flex', gap: 20 }}>
                  <span>Sales <b style={{ color: SYS_GREEN }}>{fmt(filtered.filter(t => t.type === 'sale').reduce((s,t) => s+t.amount, 0))}</b></span>
                  <span>Expenses <b style={{ color: SYS_RED }}>{fmt(filtered.filter(t => t.type === 'expense').reduce((s,t) => s+t.amount, 0))}</b></span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ══ OVERHEAD ════════════════════════════════════════════════════════ */}
        {tab === 'overhead' && (
          <div>
            {/* Summary banner */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 14, marginBottom: 20 }}>
              <KpiCard label="Monthly Overhead" value={fmt(monthlyOverhead)} sub={`${recurring.filter(r => r.active !== false).length} active items`} accent={SYS_ORANGE} />
              <KpiCard label="Annual Fixed Cost" value={fmt(monthlyOverhead * 12)} sub="projected" accent={SYS_RED} small />
              <KpiCard label="Daily Overhead" value={fmt(monthlyOverhead / 30.4)} sub="avg per day" accent={MUTED} small />
            </div>

            {/* Overhead by category */}
            {Object.keys(recByCat).length > 0 && (
              <div style={{ ...CARD, padding: '20px 22px', marginBottom: 20 }}>
                <SectionTitle>Breakdown by Category</SectionTitle>
                {Object.entries(recByCat).sort((a, b) => b[1] - a[1]).map(([cat, mo]) => (
                  <BarRow key={cat} label={cat} value={mo} total={monthlyOverhead} color={SYS_ORANGE} valueFmt={`${fmt(mo)}/mo`} />
                ))}
              </div>
            )}

            {/* Due This Month */}
            {dueThisMonth.length > 0 && (
              <div style={{ ...CARD, padding: '20px 22px', marginBottom: 20 }}>
                <SectionTitle>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Clock size={14} color={SYS_ORANGE} /> Due This Month</span>
                  <span style={{ fontSize: 11, color: MUTED }}>Today is the {currentDay}th</span>
                </SectionTitle>
                {dueThisMonth.map(r => {
                  const isPast = r.dueDay < currentDay;
                  const isToday = r.dueDay === currentDay;
                  return (
                    <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: '1px solid #F9F9F9' }}>
                      <div style={{ width: 36, height: 36, borderRadius: 10, background: isToday ? 'rgba(255,59,48,0.1)' : isPast ? BG : 'rgba(255,149,0,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 13, fontWeight: 700, color: isToday ? SYS_RED : isPast ? MUTED : SYS_ORANGE }}>
                        {r.dueDay}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: isToday ? SYS_RED : INK }}>{r.name}</div>
                        <div style={{ fontSize: 11, color: MUTED }}>{isToday ? 'Due today' : isPast ? `Was due on the ${r.dueDay}th` : `Due in ${r.dueDay - currentDay} day${r.dueDay - currentDay !== 1 ? 's' : ''}`}</div>
                      </div>
                      <div style={{ fontSize: 15, fontWeight: 700, color: INK, fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>{fmt(r.amount)}</div>
                    </div>
                  );
                })}
                <div style={{ paddingTop: 12, display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                  <span style={{ color: MUTED }}>Total due this month</span>
                  <span style={{ fontWeight: 700, color: INK }}>{fmt(dueThisMonth.reduce((s, r) => s + r.amount, 0))}</span>
                </div>
              </div>
            )}

            {/* List */}
            <div style={{ ...CARD, overflow: 'hidden' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 22px', borderBottom: '1px solid #F2F2F7' }}>
                <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: INK }}>Recurring Items</h3>
                <button onClick={() => setRecurModal('new')}
                  style={{ padding: '8px 16px', background: BRAND, border: 'none', borderRadius: 10, color: 'white', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontFamily: FONT }}>
                  <Plus size={13} /> Add Item
                </button>
              </div>

              {recurring.length === 0 ? (
                <div style={{ padding: 40, textAlign: 'center', color: MUTED, fontSize: 14 }}>
                  <RotateCw size={28} color="#E5E5EA" style={{ marginBottom: 10 }} />
                  <div>No overhead items yet.</div>
                  <div style={{ fontSize: 12, marginTop: 4 }}>Add rent, utilities, insurance, subscriptions…</div>
                </div>
              ) : recurring.map((r, i) => {
                const monthly = r.frequency === 'monthly' ? r.amount : r.amount / 12;
                let dueBadge = null;
                if (r.dueDay) {
                  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
                  if (r.frequency === 'monthly') {
                    const isPast = r.dueDay < currentDay;
                    const isToday = r.dueDay === currentDay;
                    dueBadge = (
                      <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 6, background: isToday ? 'rgba(255,59,48,0.12)' : isPast ? '#F2F2F7' : 'rgba(255,149,0,0.12)', color: isToday ? SYS_RED : isPast ? MUTED : SYS_ORANGE }}>
                        Due {isToday ? 'today' : isPast ? `was ${r.dueDay}th` : `${r.dueDay}th`}
                      </span>
                    );
                  } else if (r.dueMonth) {
                    dueBadge = (
                      <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 6, background: '#F2F2F7', color: MUTED }}>
                        Due {months[r.dueMonth - 1]} {r.dueDay}
                      </span>
                    );
                  }
                }
                return (
                  <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 22px', borderBottom: i < recurring.length - 1 ? '1px solid #F9F9F9' : 'none', opacity: r.active === false ? 0.45 : 1 }}>
                    <div style={{ width: 36, height: 36, borderRadius: 10, background: r.active !== false ? '#FFF8ED' : BG, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <RotateCw size={16} color={r.active !== false ? SYS_ORANGE : MUTED} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                        <span style={{ fontSize: 14, fontWeight: 600, color: INK }}>{r.name}</span>
                        {dueBadge}
                      </div>
                      <div style={{ fontSize: 12, color: MUTED }}>{r.category} · {r.frequency === 'monthly' ? 'monthly' : 'annual'}{r.notes ? ` · ${r.notes}` : ''}</div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontSize: 16, fontWeight: 700, color: INK, fontVariantNumeric: 'tabular-nums' }}>{fmt(r.amount)}</div>
                      <div style={{ fontSize: 11, color: MUTED }}>{r.frequency === 'annual' ? `${fmt(monthly)}/mo` : '/month'}</div>
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                      <button onClick={() => setRecurModal(r)} style={{ padding: '6px 10px', border: '1px solid #E5E5EA', borderRadius: 8, background: 'white', cursor: 'pointer', color: SECONDARY, fontSize: 11, fontFamily: FONT }}><Edit2 size={11} /></button>
                      <button onClick={() => handleDeleteRecurring(r)} style={{ padding: '6px 10px', border: '1px solid #FFD0CC', borderRadius: 8, background: 'white', cursor: 'pointer', color: SYS_RED, fontSize: 11, fontFamily: FONT }}><Trash2 size={11} /></button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ══ REPORTS ═════════════════════════════════════════════════════════ */}
        {tab === 'reports' && canReport && (
          <div>
            <div style={{ marginBottom: 20 }}><PeriodPicker /></div>

            {/* P&L */}
            <div style={{ ...CARD, padding: '22px 24px', marginBottom: 16 }}>
              <SectionTitle>Profit & Loss Statement</SectionTitle>

              {[
                { label: 'Gross Revenue', val: allSales.reduce((s,t) => s+t.amount, 0), color: SECONDARY, indent: 0, bold: false },
                { label: 'Less: Refunds & Returns', val: -refunds, color: SYS_ORANGE, indent: 16, bold: false },
                { label: 'Net Revenue (collected)', val: revenue, color: INK, indent: 0, bold: true, divider: true },
                { label: 'Less: Variable Job Costs', val: -jobExpenses, color: SYS_RED, indent: 16, bold: false, note: pct(jobExpenses, revenue) + ' of rev' },
                { label: 'Gross Profit', val: grossProfit, color: grossProfit >= 0 ? BRAND : SYS_RED, indent: 0, bold: true, divider: true },
                { label: `Less: Fixed Overhead (${oMonths ? oMonths.toFixed(1) + ' mo' : 'monthly rate'})`, val: -overheadForPeriod, color: SYS_ORANGE, indent: 16, bold: false, note: oMonths ? pct(overheadForPeriod, revenue) + ' of rev' : '' },
                { label: 'Net Profit', val: netProfit, color: netProfit >= 0 ? SYS_BLUE : SYS_RED, indent: 0, bold: true },
              ].map(({ label, val, color, indent, bold, divider, note }, i) => (
                <div key={i}>
                  {divider && <div style={{ height: 1, background: '#F2F2F7', margin: '4px 0' }} />}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 0', paddingLeft: indent }}>
                    <span style={{ fontSize: 13, color: SECONDARY, fontWeight: bold ? 700 : 400 }}>{label}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      {note && <span style={{ fontSize: 11, color: MUTED }}>{note}</span>}
                      <span style={{ fontSize: bold ? 16 : 14, fontWeight: bold ? 800 : 500, color, fontVariantNumeric: 'tabular-nums' }}>{fmt(val)}</span>
                    </div>
                  </div>
                </div>
              ))}

              <div style={{ borderTop: '2px solid #E5E5EA', marginTop: 8, paddingTop: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: 12, color: MUTED, marginBottom: 2 }}>Gross Margin</div>
                  <span style={{ fontSize: 22, fontWeight: 800, color: grossMargin >= 20 ? BRAND : grossMargin >= 0 ? SYS_ORANGE : SYS_RED }}>{grossMargin.toFixed(1)}%</span>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 12, color: MUTED, marginBottom: 2 }}>Net Margin</div>
                  <span style={{ fontSize: 22, fontWeight: 800, color: netMargin >= 15 ? SYS_BLUE : netMargin >= 0 ? SYS_ORANGE : SYS_RED }}>{netMargin.toFixed(1)}%</span>
                </div>
              </div>
            </div>

            {/* KPI tiles */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(155px, 1fr))', gap: 12, marginBottom: 16 }}>
              {[
                { label: 'Collection Rate', val: collRate.toFixed(1)+'%', sub: `${fmt(pendingTotal)} pending`, color: collRate >= 90 ? SYS_GREEN : SYS_ORANGE },
                { label: 'Expense Ratio', val: revenue > 0 ? (jobExpenses/revenue*100).toFixed(1)+'%' : '—', sub: 'job costs / revenue', color: revenue > 0 && jobExpenses/revenue < 0.65 ? SYS_GREEN : SYS_ORANGE },
                { label: 'Avg Sale', val: fmt(avgSale), sub: `${paidSales.length} paid sales`, color: SYS_BLUE },
                { label: 'Largest Sale', val: paidSales.length ? fmt(Math.max(...paidSales.map(t => t.amount))) : '—', sub: 'single transaction', color: SYS_PURPLE },
                { label: 'Daily Revenue', val: fmt(dailyRevenue), sub: `${periodDays}d period`, color: SYS_GREEN },
                { label: 'Daily Overhead', val: fmt(dailyOverhead), sub: 'fixed cost/day', color: SYS_ORANGE },
                { label: 'Daily Net', val: fmt(dailyNet), sub: 'after overhead', color: dailyNet >= 0 ? SYS_BLUE : SYS_RED },
                ...(breakevenDay !== null ? [{ label: 'Breakeven Day', val: `Day ${breakevenDay}`, sub: breakevenDay <= currentDay ? 'already passed ✓' : `${breakevenDay - currentDay}d away`, color: breakevenDay <= currentDay ? SYS_GREEN : SYS_ORANGE }] : []),
              ].map(({ label, val, sub, color }) => (
                <KpiCard key={label} label={label} value={val} sub={sub} accent={color} small />
              ))}
            </div>

            {/* Revenue + Expense breakdown side by side */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16, marginBottom: 16 }}>
              <div style={{ ...CARD, padding: '20px 22px' }}>
                <SectionTitle><span style={{ color: SYS_GREEN }}>Revenue by Category</span></SectionTitle>
                {revCatList.length === 0 ? <p style={{ color: MUTED, fontSize: 13 }}>No sales data.</p>
                  : revCatList.map(([cat, total]) => <BarRow key={cat} label={cat} value={total} total={revenue} color={SYS_GREEN} />)}
              </div>
              <div style={{ ...CARD, padding: '20px 22px' }}>
                <SectionTitle><span style={{ color: SYS_RED }}>Job Expense Breakdown</span></SectionTitle>
                {expCatList.length === 0 ? <p style={{ color: MUTED, fontSize: 13 }}>No expense data.</p>
                  : expCatList.map(([cat, total]) => <BarRow key={cat} label={cat} value={total} total={jobExpenses} color={SYS_RED} />)}
              </div>
            </div>

            {/* Overhead in reports */}
            {recurring.length > 0 && (
              <div style={{ ...CARD, padding: '20px 22px', marginBottom: 16 }}>
                <SectionTitle><span style={{ color: SYS_ORANGE }}>Fixed Overhead Breakdown</span></SectionTitle>
                {Object.entries(recByCat).sort((a,b) => b[1]-a[1]).map(([cat, mo]) => (
                  <BarRow key={cat} label={cat} value={mo} total={monthlyOverhead} color={SYS_ORANGE} valueFmt={`${fmt(mo)}/mo`} />
                ))}
              </div>
            )}

            {/* Top customers */}
            <div style={{ ...CARD, padding: '20px 22px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                <Users size={14} color={SYS_BLUE} />
                <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: INK }}>Top Customers — Revenue Contribution</h3>
              </div>
              {topCust.length === 0 ? <p style={{ color: MUTED, fontSize: 13 }}>No sales data.</p> : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #E5E5EA' }}>
                      {['#','Customer','Revenue','% of Total','Transactions'].map(h => (
                        <th key={h} style={{ textAlign: 'left', padding: '6px 10px', fontSize: 11, color: MUTED, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', fontFamily: FONT }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {topCust.map(([name, total], i) => {
                      const cnt = paidSales.filter(t => (t.customerName?.trim() || 'Unknown') === name).length;
                      return (
                        <tr key={name} style={{ borderBottom: '1px solid #F9F9F9' }}>
                          <td style={{ padding: '9px 10px', fontWeight: 700, color: i === 0 ? SYS_ORANGE : MUTED }}>#{i+1}</td>
                          <td style={{ padding: '9px 10px', fontWeight: 600, color: INK }}>{name}</td>
                          <td style={{ padding: '9px 10px', fontWeight: 700, color: SYS_BLUE, fontVariantNumeric: 'tabular-nums' }}>{fmt(total)}</td>
                          <td style={{ padding: '9px 10px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <div style={{ height: 4, background: '#E5E5EA', borderRadius: 2, width: 70, overflow: 'hidden' }}>
                                <div style={{ height: '100%', background: SYS_BLUE, width: `${(total/revenue)*100}%`, borderRadius: 2 }} />
                              </div>
                              <span style={{ fontSize: 12, color: SECONDARY }}>{pct(total, revenue)}</span>
                            </div>
                          </td>
                          <td style={{ padding: '9px 10px', color: MUTED }}>{cnt}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}
      </div>

      {txnModal && (
        <TransactionModal transaction={txnModal === 'new' ? null : txnModal} onSave={handleSaveTxn} onClose={() => setTxnModal(null)} />
      )}
      {recurModal && (
        <RecurringModal item={recurModal === 'new' ? null : recurModal} onSave={handleSaveRecurring} onClose={() => setRecurModal(null)} />
      )}
    </div>
  );
}
